import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNetwork, useNotification, useTimer } from 'hooks';
import { message as errorMessage, validateEmail } from 'constant';
import {
	ApprovalStatusState,
	CountryCode,
	LoginCredsState,
	LoginMaskPhoneCredsState,
	LoginStep,
	VerificationIDState,
	useWebLogin,
} from '../../store';
import { WEBAUTH_URL } from 'components/login/store';
import {
	ApprovalChannel,
	ApprovalChannelKind,
	maskEmail,
} from '../approval-channel';

import './biometric-approval-sent.scss';

/**
 * Step BIOMETRIC_APPROVAL — the approval that precedes registering a passkey
 * on this browser ("Set up a passkey" on the passkey card).
 *
 * Choose Method no longer sends the link before landing here; this screen
 * asks which channel to use, sends ONLY that one (`channel` on the request),
 * and then polls `verify-phone/:id` exactly as it always did. The polling,
 * the resend schedule and the Intercom fallback are the pre-existing logic.
 */
export const BiometricApprovalSent = () => {
	// globel state
	const country = useRecoilValue(CountryCode);
	const maskPhoneCreds = useRecoilValue(LoginMaskPhoneCredsState);
	const setStep = useSetRecoilState(LoginStep);
	const verificationID = useRecoilValue(VerificationIDState);
	const setVerificationStatus = useSetRecoilState(ApprovalStatusState);
	const phone = useRecoilValue(LoginCredsState);

	// local state
	const [checkStatus, setCheckStatus] = useState(true);
	const [count, setCount] = useState(1);
	const [sentChannel, setSentChannel] = useState<ApprovalChannelKind | null>(
		null
	);
	const [busy, setBusy] = useState<ApprovalChannelKind | null>(null);
	// verify-phone/:id now answers 200 status 'expired' when the record is gone
	// (7-day TTL, or already used) instead of a 400. Stop polling and offer a
	// fresh link rather than bouncing back to Choose Method with a toast.
	const [expired, setExpired] = useState(false);

	// hooks
	const { get } = useNetwork({ updateState: false, returnResponse: true });
	const { errorNotification, successNotification } = useNotification();
	const { seconds, setSeconds } = useTimer();
	const { registrationApproval } = useWebLogin();

	const isResendAllowed = useMemo(() => !seconds, [seconds]);

	// The request Choose Method used to send, plus the channel the person
	// picked (`registrationApproval` adds `rpId` itself).
	const buildPayload = useCallback(
		(channel: ApprovalChannelKind) => {
			const contactInfo = validateEmail(phone)
				? { email: phone }
				: { phone, countryCode: country };
			return {
				type: 'registrationApproval',
				deviceInfo: navigator.userAgent ?? {},
				...contactInfo,
				channel,
			};
		},
		[country, phone]
	);

	// Initial send, or a switch to the other channel.
	const sendVia = useCallback(
		async (channel: ApprovalChannelKind) => {
			if (busy) return;
			setBusy(channel);
			try {
				const response = await registrationApproval(buildPayload(channel));
				if (!response) return;
				const { message, success, verificationId, errorData } = response;
				if (success && verificationId) {
					setExpired(false);
					const used: ApprovalChannelKind =
						response.channel === 'email' || response.channel === 'phone'
							? response.channel
							: channel;
					setSentChannel(used);
					setSeconds(20);
					successNotification('Link sent successfully');
				} else {
					errorNotification(
						message ||
							(errorData?.message ?? errorMessage.SomethingWentWrongMessage)
					);
				}
			} finally {
				setBusy(null);
			}
		},
		[
			busy,
			buildPayload,
			errorNotification,
			registrationApproval,
			setSeconds,
			successNotification,
		]
	);

	const handleResendOTP = useCallback(async () => {
		if (!isResendAllowed || count > 3 || !sentChannel || busy) return;

		setCount(prev => prev + 1);
		if (count === 1) {
			setSeconds(40);
		} else if (count === 2) {
			setSeconds(60);
		}

		setBusy(sentChannel);
		try {
			const response = await registrationApproval(buildPayload(sentChannel));
			if (!response) return;

			const { message, success, verificationId, errorData } = response;
			if (success && verificationId) {
				successNotification(message);
			} else {
				const errorMessageToShow =
					message ||
					(errorData?.message ?? errorMessage.SomethingWentWrongMessage);
				errorNotification(errorMessageToShow);
			}
		} finally {
			setBusy(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isResendAllowed,
		count,
		sentChannel,
		busy,
		buildPayload,
		registrationApproval,
	]);

	const onClickIntercom = () => {
		(window as any)?.Intercom?.(
			'showNewMessage',
			'Unable to Login, Not receiving message'
		);
	};

	// handle back
	const handleClickBtn = useCallback(() => {
		setStep('VERIFY');
	}, [setStep]);

	const verifyPhone = useCallback(async () => {
		const verficationUrl = `${WEBAUTH_URL.CLIENT_VERIFY_PHONE}/${verificationID}`;
		const res = await get(verficationUrl);

		const { status } = res?.apiPayload?.data ?? {};
		if (status === 'expired') {
			setExpired(true);
			return;
		}
		if (status === 'rejected' || status === 'approved') {
			setStep('VERIFY');
		}
		if (res?.errorCode === 400 && res?.errorData?.message) {
			errorNotification(res.errorData.message);
			setStep('VERIFY');
		}
		setVerificationStatus(status);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [get, setStep, setVerificationStatus, verificationID]);

	useEffect(() => {
		// Nothing left to poll for once the link is spent.
		if (expired) return;
		if (verificationID !== '') {
			verifyPhone();
		}
		const id = setTimeout(() => {
			setCheckStatus(pre => !pre);
		}, 4000);
		return () => clearTimeout(id);
		// eslint-disable-next-line
	}, [checkStatus, verificationID, expired]);

	const emailDisplay = useMemo(
		() => (validateEmail(phone) ? maskEmail(phone) : undefined),
		[phone]
	);
	const identity = emailDisplay ?? `${country} ${phone}`.trim();
	const phoneDisplay = `${country} ${maskPhoneCreds}`.trim();

	// (the securityCode block that used to render beside the spinner is kept
	// for reference, as it was in the old screen:)
	// {/* <div className="biometric-approval__number">{securityCode}</div>
	//     <div className="biometric-approval__specification">
	//       Tap <strong>{securityCode}</strong> on your phone to verify your
	//       phone number{" "}
	//     </div> */}
	return (
		<div className="biometric-approval">
			<div className="biometric-approval__wrapper biometric-approval__wrapper--sv2">
				<ApprovalChannel
					purpose="passkey"
					identity={identity}
					phoneDisplay={phoneDisplay}
					emailDisplay={emailDisplay}
					sent={sentChannel}
					expired={expired}
					busy={busy}
					resend={{
						allowed: isResendAllowed,
						seconds,
						exhausted: count === 4,
					}}
					onSend={sendVia}
					onResend={handleResendOTP}
					onBack={handleClickBtn}
					onSupport={onClickIntercom}
				/>
			</div>
		</div>
	);
};
